import { useEffect, useMemo } from 'react';

import { IoCheckmarkCircle } from 'react-icons/io5';

import { useRouter } from 'next/router';

import { useQuery } from '@tanstack/react-query';

import { satoshi } from '~/common/styles/fonts';
import { getPublicBusinessBySlug } from '~/common/services/controllers/business/index';
import { useCustomTheme } from '~/common/providers/theme';
import { useBusiness } from '~/common/providers/business';
import { IFCWithChildren } from '~/common/interfaces/i-fc-with-children';
import { getBusinessLogo } from '~/common/constants/vmcore-brand';
import { Typography } from '~/common/components/Typography';
import { SplashLoader } from '~/common/components/SplashLoader';
import { MonteiroEngenhariaLogo } from '~/common/components/MonteiroEngenhariaLogo';

import {
  BrandContent,
  BrandEyebrow,
  BrandGrid,
  BrandHeadline,
  BrandList,
  BrandOrb,
  BrandPanel,
  BrandRings,
  Content,
  Footer,
  FormSide,
  LoginCard,
  LogoSlot,
  PublicLayoutContainer,
  Wrapper,
} from './styles';

/* Copy genérica do produto — vale para qualquer instância whitelabel. */
const brandFeatures = [
  'Obras, RDOs e cronogramas',
  'Folha, ponto e RH integrados',
  'Compras, pedidos e notas fiscais',
  'Indicadores em tempo real',
];

export const PublicLayout: IFCWithChildren = ({ children }) => {
  const router = useRouter();
  const { setBusiness } = useBusiness();
  const { themeOption } = useCustomTheme();

  const businessSlug = useMemo(() => {
    const currentPath = router.asPath.split('?')[0].replace(/\/+$/, '');
    const pathSegments = currentPath.split('/').filter(Boolean);

    return pathSegments[pathSegments.length - 1] || undefined;
  }, [router.asPath]);

  const { data: publicBusiness, isLoading } = useQuery({
    queryKey: ['public-business', businessSlug || 'monteiro'],
    queryFn: async () => getPublicBusinessBySlug(businessSlug),
  });
  const copyright = publicBusiness?.configuracoes?.copyright?.trim();
  const businessLogo = getBusinessLogo(
    publicBusiness,
    themeOption === 'dark' ? 'light' : 'dark'
  );

  useEffect(() => {
    setBusiness(publicBusiness || null);
  }, [publicBusiness, setBusiness]);

  if (isLoading || !publicBusiness) {
    return <SplashLoader loaderColor="neutral.100" showLogo={false} />;
  }

  return (
    <PublicLayoutContainer>
      <BrandPanel aria-hidden="true">
        <BrandGrid />
        <BrandOrb $variant={1} />
        <BrandOrb $variant={2} />
        <BrandRings viewBox="0 0 440 440">
          <circle
            cx="220"
            cy="220"
            r="120"
            fill="none"
            stroke="rgba(255,255,255,0.09)"
            strokeWidth="1"
          />
          <circle
            cx="220"
            cy="220"
            r="165"
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
          <circle
            cx="220"
            cy="220"
            r="210"
            fill="none"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        </BrandRings>

        <BrandContent className={satoshi.className}>
          <BrandEyebrow>Gestão para engenharia</BrandEyebrow>
          <BrandHeadline>Sua operação inteira, em um só lugar.</BrandHeadline>
          <BrandList>
            {brandFeatures.map((feature) => (
              <li key={feature}>
                <IoCheckmarkCircle size={18} />
                {feature}
              </li>
            ))}
          </BrandList>
        </BrandContent>
      </BrandPanel>

      <FormSide>
        <Wrapper id="public-layout-wrapper-id">
          <Content id="public-layout-content-id">
            <LoginCard>
              {businessLogo ? (
                <LogoSlot>
                  <MonteiroEngenhariaLogo
                    src={businessLogo}
                    alt={publicBusiness.nome}
                    width={230}
                    height={64}
                    fallbackToDefaultLogo={false}
                  />
                </LogoSlot>
              ) : null}
              {children}
            </LoginCard>
          </Content>
          <Footer>
            <Typography.Label size="x-small" color="neutral.500">
              {copyright ? `${copyright} | ` : ''}Desenvolvido por &copy;{' '}
              <strong>VMCORE</strong>
            </Typography.Label>
          </Footer>
        </Wrapper>
      </FormSide>
    </PublicLayoutContainer>
  );
};
